import React, { useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell,
} from "recharts";
import {
  Download, TrendingUp, Inbox, CheckCircle2, Activity, Lightbulb, BarChart3,
  ArrowUpRight, ArrowDownRight, Star, ClipboardCheck, FileText, ArrowRight, FileBarChart, WifiOff, LayoutTemplate, Send,
} from "lucide-react";
import api from "@/lib/api";
import { useCockpit } from "./CockpitLayout";
import { Card, SectionLabel, Pill, Sparkline, fmt, AnalyticsOffline } from "./ui";
import { Spinner, Empty } from "@/components/common";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { TrackingConnect } from "./TrackingConnect";
import useCockpitReports from "./useCockpitReports";

const TONE = { lime: "#A3E635", blue: "#3B82F6", green: "#22C55E", purple: "#8B5CF6" };
const GREEN = "#22C55E";
const RED = "#F87171";

export default function CReports() {
  const { accent, clientId, base } = useCockpit();
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const [selection, setSelection] = useState(null);
  const { data, loadError, load, scopeKey } = useCockpitReports({ lang, clientId,
    userId: user?.id, accountClientId: user?.client_id });

  if (loadError) return (
    <div role="alert" data-testid="creports-load-error" className="p-5">
      <p>{t("Monatsreports konnten nicht geladen werden.")}</p>
      <button onClick={load} data-testid="creports-load-retry" className="mt-3 min-h-[44px] underline">{t("Erneut versuchen")}</button>
    </div>
  );
  if (!data) return <Spinner />;
  const reports = data.reports || [];
  const active = selection?.scopeKey === scopeKey ? Math.max(0, reports.findIndex(r => r.id === selection.id)) : 0;
  const setActive = index => setSelection({ scopeKey, id: reports[index]?.id || null });
  const live = !!data.live;
  const demo = !!data.demo;

  const downloadPdf = async (r) => {
    try {
      const res = await api.get(`/reports/${r.id}/pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error(t("PDF konnte nicht geladen werden.")); }
  };

  if (reports.length === 0) return (
    <div className="fade-in">
      <p className="text-sm font-semibold flex items-center gap-1.5" style={{ color: accent }}><BarChart3 className="w-4 h-4" /> {t("Statistik")}</p>
      <h1 className="text-[26px] lg:text-[34px] leading-tight font-black tracking-tight mt-1 mb-1">{t("Monatsreport")}</h1>
      <p className="text-sm text-gray-400 mb-5">{t("Ihre monatliche Auswertung — sobald verfügbar erscheint sie hier.")}</p>
      <Empty text={t("Noch kein Monatsreport veröffentlicht.")} hint={t("Sobald KEWAG Ihren ersten Report erstellt, sehen Sie ihn hier.")} />
    </div>
  );

  return <ReportView key={reports[active]?.id} r={reports[active]} reports={reports} active={active} setActive={setActive}
    live={live} demo={demo} liveLabel={data.live_label} accent={accent} t={t} nav={nav} base={base} downloadPdf={downloadPdf}
    clientId={clientId} reload={load} />;
}

function ReportView({ r, reports, active, setActive, live, demo, liveLabel, accent, t, nav, base, downloadPdf, clientId, reload }) {
  const prevLabel = r.prev_month_label;
  const subVs = prevLabel ? `${t("vs.")} ${prevLabel}` : (r.month_label || "");
  const uptime = r.uptime_pct;
  const chrono = [...reports].reverse();
  const leadsHist = chrono.map((x) => x.leads_count ?? 0);
  const tasksHist = chrono.map((x) => x.completed_tasks ?? 0);
  const uptimeHist = chrono.map((x) => x.uptime_pct ?? 0);

  const kpis = [
    { key: "visits", icon: TrendingUp, tone: "lime", value: live ? fmt(r.visits) : "—", delta: live ? r.visits_delta : null, series: live ? (r.visits_series || []).map((p) => p.v) : [], label: t("Besuche") },
    { key: "leads", icon: Inbox, tone: "blue", value: r.leads_count ?? 0, delta: r.leads_delta, series: leadsHist, label: t("Neue Anfragen") },
    { key: "tasks", icon: CheckCircle2, tone: "green", value: r.completed_tasks ?? 0, delta: r.tasks_delta, series: tasksHist, label: t("Aufgaben erledigt") },
    { key: "uptime", icon: Activity, tone: "purple", value: (live && uptime != null) ? `${uptime}%` : "—", delta: live ? r.uptime_delta : null, series: live ? uptimeHist : [], label: t("Verfügbarkeit") },
  ];

  // Zeitverlauf-Chart-Daten
  const chart = (r.visits_series || []).map((p) => {
    const d = new Date(p.d);
    return { v: p.v, label: d.toLocaleDateString("de-CH", { day: "numeric", month: "short" }), full: d.toLocaleDateString("de-CH", { day: "numeric", month: "short", year: "numeric" }) };
  });

  const highlights = [
    `${r.leads_count ?? 0} ${t("neue Anfragen")}`,
    `${r.completed_tasks ?? 0} ${t("Aufgaben erledigt")}`,
    live ? `${fmt(r.visits)} ${t("Website-Besuche")}` : null,
    (live && uptime != null) ? `${uptime}% ${t("Verfügbarkeit")}` : null,
  ].filter(Boolean);

  const openPoints = (r.open_points && !/kein/i.test(r.open_points))
    ? r.open_points.split(/[,;]/).map((s) => s.trim()).filter(Boolean) : [];

  const topPages = r.top_pages || [];
  const topMax = Math.max(1, ...topPages.map((p) => p.views || 0));
  const sources = r.sources || [];
  const SRC = {
    google: { name: "Google", color: TONE.lime },
    direct: { name: t("Direkt"), color: TONE.blue },
    social: { name: t("Social Media"), color: TONE.purple },
    other: { name: t("Andere"), color: "#9ca3af" },
  };
  const donut = sources.map((s) => ({ ...s, name: SRC[s.key]?.name || s.key, color: SRC[s.key]?.color || "#9ca3af" }));

  const reviewsRecv = r.reviews_received || [];
  const reviewsPlan = r.reviews_planned || [];
  const designIdeas = r.design_ideas || [];
  const stars = (n) => "★".repeat(n || 0) + "☆".repeat(Math.max(0, 5 - (n || 0)));

  return (
    <div className="fade-in space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm font-semibold flex items-center gap-1.5" style={{ color: accent }}><BarChart3 className="w-4 h-4" /> {t("Statistik")}</p>
          <h1 className="text-[26px] lg:text-[34px] leading-tight font-black tracking-tight mt-1">{t("Monatsreport")}</h1>
          <p className="text-sm text-gray-400 mt-1.5">{t("Ihre monatliche Auswertung auf einen Blick.")}</p>
        </div>
        <button onClick={() => downloadPdf(r)} data-testid="creport-pdf"
          className="shrink-0 inline-flex items-center gap-2 font-semibold rounded-xl px-4 py-2.5 border bg-white/[0.03] hover:bg-white/[0.07] text-gray-100 transition-colors"
          style={{ borderColor: `${accent}55` }}>
          <Download className="w-4 h-4" style={{ color: accent }} /> {t("Als PDF exportieren")}
        </button>
      </div>

      {/* ECHT-Regel: Live-Badge nur bei verbundener Analyse, sonst ehrlicher Status */}
      {live ? (
        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold px-3 py-1 rounded-full border border-[#A3E635]/40 bg-[#A3E635]/10 text-[#A3E635]" data-testid="creport-live-badge">
          <span className="w-1.5 h-1.5 rounded-full bg-[#A3E635]" /> {liveLabel ? t(liveLabel) : t("Live-Daten")}
        </span>
      ) : (
        <div className="flex items-center gap-2.5 flex-wrap">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-bold px-3 py-1 rounded-full border border-white/15 bg-white/5 text-gray-400" data-testid="creport-notconnected-badge">
            <WifiOff className="w-3 h-3" /> {t("Analyse nicht verbunden")}
          </span>
          <TrackingConnect clientId={clientId} accent={accent} onConnected={reload} variant="link" label={t("Jetzt verbinden")} />
        </div>
      )}
      {!live && (
        <p className="text-xs text-gray-500 -mt-2" data-testid="creport-notconnected-note">
          {t("Hinweis: Besuche, Verlauf, Beliebteste Seiten und Verfügbarkeit erscheinen mit echten Zahlen, sobald Ihre Website-Analyse verbunden ist. Neue Anfragen und erledigte Aufgaben sind echt aus Ihrer Datenbank.")}
        </p>
      )}

      {/* Monats-Auswahl */}
      {reports.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
          {reports.map((rep, i) => (
            <Pill key={rep.id} active={i === active} accent={accent} onClick={() => setActive(i)} testid={`creport-month-${i}`}>{rep.month_label || rep.month}</Pill>
          ))}
        </div>
      )}

      {/* KPI-Karten */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4" data-testid="creport-metrics">
        {kpis.map(({ key, ...k }) => <ReportKpi key={key} {...k} sub={subVs} testid={`creport-kpi-${key}`} />)}
      </div>

      {/* Zeitverlauf + Highlights/Offene Punkte/Empfehlung */}
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_minmax(0,420px)] gap-5 items-start">
        <Card className="p-5" data-testid="creport-chart">
          <div className="flex items-center justify-between gap-3 mb-3">
            <SectionLabel>{t("Besuche im Zeitverlauf")}</SectionLabel>
            <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-300 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 shrink-0">
              {t("Letzte 30 Tage")}
            </div>
          </div>
          {live ? (
          <ResponsiveContainer width="100%" height={230}>
            <AreaChart data={chart} margin={{ top: 8, right: 8, bottom: 0, left: -8 }}>
              <defs>
                <linearGradient id="crep-grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={accent} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={accent} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="label" interval={5} tick={{ fill: "#9ca3af", fontSize: 11 }} axisLine={false} tickLine={false} dy={4} />
              <YAxis tick={{ fill: "#9ca3af", fontSize: 11 }} axisLine={false} tickLine={false} width={34} allowDecimals={false} />
              <Tooltip cursor={{ stroke: "rgba(255,255,255,0.12)" }}
                contentStyle={{ background: "#121217", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, color: "#fff", fontSize: 12 }}
                labelFormatter={(_, p) => p?.[0]?.payload?.full || ""} formatter={(v) => [fmt(v), t("Besuche")]} />
              <Area type="monotone" dataKey="v" stroke={accent} strokeWidth={2.5} fill="url(#crep-grad)" dot={{ r: 2.5, fill: accent }} activeDot={{ r: 5 }} />
            </AreaChart>
          </ResponsiveContainer>
          ) : (
            <AnalyticsOffline t={t} minH={230} testid="creport-chart-offline" action={<TrackingConnect clientId={clientId} accent={accent} onConnected={reload} />} />
          )}
        </Card>

        <div className="space-y-5 min-w-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {/* Highlights */}
            <Card className="p-5" data-testid="creport-highlights">
              <div className="flex items-center gap-2 mb-3"><Star className="w-4 h-4" style={{ color: accent }} /><SectionLabel>Highlights</SectionLabel></div>
              <div className="space-y-2.5">
                {highlights.map((h, i) => (
                  <div key={i} className="flex items-center gap-2.5"><CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: GREEN }} /><span className="text-sm text-gray-200">{h}</span></div>
                ))}
              </div>
            </Card>

            {/* Offene Punkte */}
            <Card className="p-5" data-testid="creport-open-points">
              <div className="flex items-center gap-2 mb-3"><ClipboardCheck className="w-4 h-4 text-amber-400" /><SectionLabel>{t("Offene Punkte")}</SectionLabel></div>
              {openPoints.length ? (
                <div className="space-y-2.5">
                  {openPoints.map((p, i) => (
                    <div key={i} className="flex items-start gap-2.5"><span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 shrink-0" /><span className="text-sm text-gray-300">{p}</span></div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center text-center py-4">
                  <span className="w-12 h-12 rounded-full flex items-center justify-center border border-white/10 bg-white/5 mb-2"><CheckCircle2 className="w-6 h-6 text-gray-500" /></span>
                  <span className="text-sm text-gray-400">{t("Keine offenen Punkte")}</span>
                </div>
              )}
            </Card>
          </div>

          {/* Empfehlung */}
          <Card className="p-5" style={{ background: `${accent}0D`, borderColor: `${accent}33` }} data-testid="creport-recommendation">
            <div className="flex items-center justify-between gap-3 mb-2">
              <div className="flex items-center gap-2"><Lightbulb className="w-4 h-4" style={{ color: accent }} /><SectionLabel>{t("Unsere Empfehlung")}</SectionLabel></div>
              <button onClick={() => nav(`${base}/next-steps`)} data-testid="creport-reco-btn"
                className="inline-flex items-center gap-1 text-xs font-bold text-black rounded-lg px-3 py-1.5 shrink-0 active:scale-95 transition-transform" style={{ background: accent }}>
                {t("Zur Analyse")} <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
            <p className="text-sm text-gray-200">{r.recommendation}</p>
          </Card>
        </div>
      </div>

      {/* Beliebteste Seiten + Traffic-Quellen */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card className="p-5" data-testid="creport-top-pages">
          <div className="flex items-center justify-between gap-3 mb-4">
            <SectionLabel>{t("Beliebteste Seiten")}</SectionLabel>
            <div className="text-xs font-semibold text-gray-300 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 shrink-0">{t("Nach Besuchen")}</div>
          </div>
          {topPages.length ? (
            <div className="space-y-3.5">
              {topPages.map((p, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="w-5 text-sm font-bold text-gray-500 shrink-0 text-center">{i + 1}</span>
                  <span className="text-sm text-gray-300 w-32 sm:w-40 truncate shrink-0">{p.page}</span>
                  <div className="flex-1 h-2 rounded-full bg-white/5 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${Math.round((p.views / topMax) * 100)}%`, background: accent }} /></div>
                  <span className="text-sm font-bold shrink-0 w-12 text-right">{fmt(p.views)}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center text-center py-10">
              <span className="w-11 h-11 rounded-xl flex items-center justify-center bg-white/5 border border-white/10 mb-3"><FileText className="w-5 h-5 text-gray-500" /></span>
              <div className="text-sm font-semibold text-gray-300">{t("Noch keine Daten")}</div>
              <p className="text-xs text-gray-500 mt-1 max-w-[260px]">{t("Sobald Ihre Website-Analyse verbunden ist, erscheinen hier Ihre Top-Seiten.")}</p>
            </div>
          )}
        </Card>

        <Card className="p-5" data-testid="creport-sources">
          <SectionLabel>{t("Traffic-Quellen")}</SectionLabel>
          {donut.length ? (
            <div className="flex items-center gap-5 flex-wrap sm:flex-nowrap mt-2">
              <div className="relative shrink-0" style={{ width: 168, height: 168 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={donut} dataKey="pct" nameKey="name" cx="50%" cy="50%" innerRadius={56} outerRadius={80} paddingAngle={2} stroke="none">
                      {donut.map((d, i) => <Cell key={i} fill={d.color} />)}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <span className="text-2xl font-black tracking-tight leading-none">{fmt(r.visits)}</span>
                  <span className="text-[11px] text-gray-500 mt-0.5">{t("Besuche")}</span>
                </div>
              </div>
              <div className="flex-1 min-w-[160px] space-y-2.5">
                {donut.map((d, i) => (
                  <div key={i} className="flex items-center gap-2.5 text-sm">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: d.color }} />
                    <span className="text-gray-300 flex-1">{d.name}</span>
                    <span className="font-bold">{d.pct}%</span>
                    <span className="text-gray-500 w-12 text-right">{fmt(d.count)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center text-center py-10">
              <span className="w-11 h-11 rounded-xl flex items-center justify-center bg-white/5 border border-white/10 mb-3"><Activity className="w-5 h-5 text-gray-500" /></span>
              <div className="text-sm font-semibold text-gray-300">{t("Noch keine Daten")}</div>
              <p className="text-xs text-gray-500 mt-1 max-w-[260px]">{t("Sobald Ihre Website-Analyse verbunden ist, sehen Sie hier die Herkunft Ihrer Besucher.")}</p>
            </div>
          )}
        </Card>
      </div>

      {/* Bewertungen & Design- & Growth-Verbesserungen (nur echte Daten mit 'Im Report'-Flag) */}
      {(reviewsRecv.length > 0 || reviewsPlan.length > 0 || designIdeas.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {(reviewsRecv.length > 0 || reviewsPlan.length > 0) && (
            <Card className="p-5" data-testid="creport-reviews">
              <div className="flex items-center gap-2 mb-4"><Star className="w-4 h-4" style={{ color: accent }} /><SectionLabel>{t("Bewertungen")}</SectionLabel></div>
              {reviewsRecv.length > 0 && (
                <div className="space-y-3">
                  {reviewsRecv.map((a, i) => (
                    <div key={i} className="rounded-xl bg-white/[0.03] border border-white/10 p-3" data-testid={`creport-review-recv-${i}`}>
                      <div className="text-amber-300 tracking-widest text-sm">{stars(a.rating)}</div>
                      {a.review_text && <p className="text-sm text-gray-300 mt-1 italic">«{a.review_text}»</p>}
                      {a.received_at && <p className="text-[11px] text-gray-500 mt-1">{a.received_at}</p>}
                    </div>
                  ))}
                </div>
              )}
              {reviewsPlan.length > 0 && (
                <div className={reviewsRecv.length > 0 ? "mt-4" : ""}>
                  <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-1.5"><Send className="w-3 h-3" /> {t("Nächste Bewertungsaktionen")}</div>
                  <div className="space-y-2">
                    {reviewsPlan.map((a, i) => (
                      <div key={i} className="flex items-center justify-between gap-3 text-sm" data-testid={`creport-review-plan-${i}`}>
                        <span className="text-gray-300 min-w-0 truncate">{a.client_hint || t("Bewertungsanfrage")}</span>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border border-amber-400/30 bg-amber-400/10 text-amber-300 shrink-0">{a.status_label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          )}
          {designIdeas.length > 0 && (
            <Card className="p-5" data-testid="creport-design">
              <div className="flex items-center gap-2 mb-4"><LayoutTemplate className="w-4 h-4" style={{ color: accent }} /><SectionLabel>{t("Website-Verbesserungen")}</SectionLabel></div>
              <div className="space-y-3">
                {designIdeas.map((d, i) => (
                  <div key={i} className="rounded-xl bg-white/[0.03] border border-white/10 p-3" data-testid={`creport-design-${i}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="font-semibold text-sm">{d.title}</span>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border border-[#A3E635]/30 bg-[#A3E635]/10 text-[#A3E635]">{d.status_label}</span>
                    </div>
                    <div className="text-[11px] text-gray-500 mt-0.5">{d.category_label}</div>
                    {d.expected_benefit && <p className="text-xs mt-1" style={{ color: accent }}>{t("Nutzen")}: {d.expected_benefit}</p>}
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Zusammenfassung */}
      <Card className="p-5" data-testid="creport-summary">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${accent}1A`, color: accent }}><FileBarChart className="w-5 h-5" /></span>
            <div className="min-w-0">
              <div className="font-bold text-sm">{t("Zusammenfassung")}</div>
              <p className="text-sm text-gray-300 mt-1">{r.summary}</p>
            </div>
          </div>
          <button onClick={() => downloadPdf(r)} data-testid="creport-detail-btn"
            className="shrink-0 inline-flex items-center gap-1.5 text-sm font-bold text-black rounded-xl px-4 py-2.5 active:scale-95 transition-transform" style={{ background: accent }}>
            {t("Detaillierter Report")} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </Card>
    </div>
  );
}

const ReportKpi = ({ icon: Icon, tone, value, delta, series, label, sub, testid }) => {
  const c = TONE[tone] || TONE.lime;
  const hasDelta = delta != null;
  const up = (delta ?? 0) >= 0;
  return (
    <Card className="p-4 lg:p-5 flex flex-col min-h-[176px] overflow-hidden" data-testid={testid}>
      <div className="flex items-center gap-3">
        <span className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${c}1F`, color: c }}><Icon className="w-5 h-5" /></span>
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <span className="text-[26px] lg:text-[32px] font-black tracking-tight leading-none">{value}</span>
          {hasDelta && (
            <span className="inline-flex items-center gap-0.5 text-[11px] font-bold px-1.5 py-0.5 rounded-md"
              style={{ color: up ? GREEN : RED, background: `${up ? GREEN : RED}1A` }}>
              {up ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}{`${up ? "+" : ""}${delta}%`}
            </span>
          )}
        </div>
      </div>
      <div className="mt-2.5">
        <div className="text-[14px] font-bold text-gray-100">{label}</div>
        <div className="text-[11px] text-gray-500 mt-0.5 truncate">{sub}</div>
      </div>
      <div className="mt-auto -mx-1 pt-2"><Sparkline data={series} color={c} height={40} /></div>
    </Card>
  );
};
